import { h, ref, nextTick } from 'vue'
import type { Component } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import http from "@/api/request"
import formCreate, { type Api, type Rule, type Options } from '@form-create/element-ui';
import type { AxiosResponse } from 'axios';

// 更精确的类型定义
interface UploadResponse {
    status: number
    data: {
        src: string
    }
}

interface ApiResponse {
    status: number
    message: string
    data: {
        rule: Rule[]
        action: string
        method: string
        title: string
        config: Options
        api?: string
        formData?: Record<string, any>
    }
}

interface FormSubmitResponse {
    status: number
    msg: string
    data: any
}

interface ModalFormConfig {
    title?: string
    /** 弹窗宽度，如 '600px'；不传则用 class 对应的全局样式 */
    width?: string
    height?: string
    /** 弹窗 class，对应 styles.scss 中 .modal-form / .upload-form 等 */
    class?: string
    /** 与 EB modal-form 一致：合并到表单初始值（如添加子菜单传 pid） */
    formData?: Record<string, any>
    /** 提交成功后回调，参数为表单数据 */
    callback?: (formData: Record<string, any>) => void
    onSuccess?: (response: FormSubmitResponse) => void
    onError?: (error: any) => void
}

const cloneJson = <T,>(val: T): T => {
    try {
        return typeof structuredClone === 'function' ? structuredClone(val) : JSON.parse(JSON.stringify(val))
    } catch {
        return JSON.parse(JSON.stringify(val))
    }
}

/**
 * EB 后端会返回 type=img + attrs.src 的“原生图片”规则。
 * Vue3 + @form-create/element-ui 下经常无法稳定渲染该类型，这里统一转为 form-create 的 html 渲染。
 */
const normalizeImgRule = (rule: any) => {
    if (!rule || typeof rule !== 'object') return
    if (rule.type !== 'img') return

    const src = String(rule?.attrs?.src || rule?.props?.src || '')
    const styleObj = { ...(rule.style || {}), ...(rule.props?.style || {}) }
    const styleStr = Object.entries(styleObj)
        .map(([k, v]) => `${k}:${String(v)}`)
        .join(';')

    // form-create/core 的 html 渲染器不会读 props.innerHTML，只会把 children 里的字符串拼成 innerHTML
    rule.type = 'html'
    rule.props = { tag: 'div' }
    rule.children = src
        ? [`<img src="${src.replace(/"/g, '&quot;')}" style="${styleStr.replace(/"/g, '&quot;')}" />`]
        : []
    delete rule.attrs
    delete rule.style
}

/** switch：className / col 须在 rule 顶层（对齐 EB），误放在 props 会导致开关不渲染 */
const normalizeSwitchRule = (rule: any) => {
    if (!rule || typeof rule !== 'object' || rule.type !== 'switch') return
    const props = rule.props
    if (!props || typeof props !== 'object') return
    if (props.className && !rule.className) {
        rule.className = props.className
        delete props.className
    }
    if (props.col != null && rule.col == null) {
        const col = props.col
        rule.col = typeof col === 'number' ? { span: col } : col
        delete props.col
    }
}

const walkRules = (rules: any[]) => {
    if (!Array.isArray(rules)) return
    for (const rule of rules) {
        normalizeImgRule(rule)
        normalizeSwitchRule(rule)
        if (Array.isArray(rule.control)) {
            for (const c of rule.control) {
                if (c && Array.isArray(c.rule)) walkRules(c.rule)
            }
        }
        if (Array.isArray(rule.children)) {
            const nested = rule.children.filter((x: any) => x && typeof x === 'object')
            if (nested.length) walkRules(nested)
        }
    }
}

/**
 * 创建模态表单
 * @param api 表单API函数
 * @param config 配置选项
 * @returns Promise
 */
export default function modalForm(
    api: () => Promise<AxiosResponse<ApiResponse>>,
    config: ModalFormConfig = {}
): Promise<FormSubmitResponse> {
    return new Promise((resolve, reject) => {
        const {
            title: configTitle,
            width,
            height,
            class: modalClass = 'modal-form',
            formData: configFormData,
            callback,
            onSuccess,
            onError,
        } = config
        const formApiRef = ref<Api | null>(null)

        // 获取表单数据
        api().then((response) => {
            const responseData = response.data.data || response.data
            const data = responseData

            if (!data.config) data.config = {}

            // 处理表单规则
            const rules = data.rule || []
            const processedRules = rules.map((rule: any) => {
                const r = cloneJson(rule)

                // 处理上传组件的回调
                if (r.type === 'frame' && r.props && r.props.type === 'image') {
                    r.props.onSuccess = (response: UploadResponse) => {
                        if (response.status === 200) {
                            return response.data.src
                        }
                        ElMessage.error('上传失败')
                        return '';
                    };
                    r.props.onLoad = (e: any) => {
                        if (e.target) {
                            e.target.style.width = '100%'
                            e.target.style.height = '100%'
                        }
                    }
                }
                // frame 的 icon 必须是 Vue 组件名，el-icon-* 为 Element UI 类名，在 Element Plus 下会解析失败，统一改为 frame 内置组件名
                if (r.type === 'frame' && r.props && typeof r.props.icon === 'string' && r.props.icon.startsWith('el-icon-')) {
                    r.props.icon = 'IconFolderOpened'
                }

                // 确保suffix正确显示
                if (r.suffix) {
                    // 保持suffix的原始结构
                    r.suffix = {
                        ...r.suffix,
                        style: {
                            ...r.suffix.style,
                            display: 'block'
                        }
                    };
                }

                // 确保inputNumber的controls正确显示
                if (r.type === 'inputNumber' && r.props) {
                    r.props = {
                        ...r.props,
                        controls: true,
                        controlsPosition: 'right'
                    };
                }

                // 商品选择(frame)：goodList 回传 { src, id }，校验改为接受 object，避免“请选择商品”误报
                if (r.type === 'frame' && r.field === 'productId' && r.props?.src?.includes('storeProduct')) {
                    r.validate = [{ required: true, type: 'object', message: '请选择商品', trigger: 'change' }];
                }

                // dateTime 转为 form-create 支持的 datetime（el-date-picker type="datetime"），否则评论时间不渲染
                if (r.type === 'dateTime') {
                    r.type = 'datetime';
                    r.props = { ...(r.props || {}), type: 'datetime', format: 'YYYY-MM-DD HH:mm:ss', valueFormat: 'YYYY-MM-DD HH:mm:ss' };
                }

                return r;
            });

            // EB: img 规则递归转换为 html（含 control 内嵌套）
            walkRules(processedRules)

            if (!data.config) data.config = {}
            if (!data.config.form) data.config.form = {}

            // 用各 rule 的 value 初始化 formData，确保每项都在 form 模型里，避免输入后仍提示「请输入」
            const initialFormData: Record<string, any> = {};
            processedRules.forEach((r: any) => {
                if (r.field == null) return;
                const hasValue = r.value !== undefined && r.value !== null && r.value !== '';
                if (hasValue || r.value === 0 || r.value === false) {
                    initialFormData[r.field] = r.value;
                } else if (r.type === 'rate' || r.type === 'inputNumber') {
                    initialFormData[r.field] = r.type === 'rate' ? (r.props?.max ?? 5) : 0;
                } else if (r.type === 'frame' && r.props?.maxLength > 1) {
                    initialFormData[r.field] = [];
                } else {
                    initialFormData[r.field] = '';
                }
            });
            // 对齐 EB modal-form.js：服务端 formData + 调用方 config.formData
            const serverFormData = data.formData ?? data.config?.formData ?? {};
            Object.assign(initialFormData, serverFormData, configFormData ?? {});
            processedRules.forEach((r: any) => {
                if (r.type === 'cascader' && r.field != null && initialFormData[r.field] !== undefined && initialFormData[r.field] !== '') {
                    const n = Number(initialFormData[r.field]);
                    if (!Number.isNaN(n)) {
                        initialFormData[r.field] = n;
                        r.value = n;
                    }
                }
            });
            if (data.config.formData) {
                data.config.formData = { ...data.config.formData, ...configFormData };
            } else {
                data.config.formData = { ...serverFormData, ...configFormData };
            }

            // 创建表单配置
            const formConfig = {
                rule: processedRules,
                option: {
                    submitBtn: false,
                    resetBtn: false,
                    form: {
                        ...(data.config?.form || {}),
                        // 对齐 EB 110px，略加宽避免「自动创建商户：」等长标签换行
                        labelWidth: data.config?.form?.labelWidth || '125px',
                    },
                    formData: initialFormData,
                    global: {
                        frame: {
                            props: {
                                closeBtn: false,
                                maskClosable: false
                            }
                        },
                        inputNumber: {
                            props: {
                                min: 0,
                                controls: true,
                                controlsPosition: 'right'
                            }
                        },
                        upload: {
                            props: {
                                accept: 'image/*',
                                maxSize: 2 * 1024 * 1024,
                                onError: (err: any) => {
                                    ElMessage.error(err.message || '上传失败')
                                }
                            }
                        }
                    }
                }
            }

            // 创建模态框
            ElMessageBox({
                title: configTitle ?? data.title ?? '表单',
                message: h('div', { class: 'form-container' }, [
                    h(formCreate, {
                        ...formConfig,
                        onMounted: (fApi: Api) => {
                            // console.log('Form mounted, fApi:', fApi)
                            formApiRef.value = fApi
                        }
                    })
                ]),
                showCancelButton: true,
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                customClass: modalClass,
                ...(width || height
                    ? {
                        customStyle: {
                            ...(width ? { width } : {}),
                            ...(height ? { maxHeight: height } : {}),
                        },
                    }
                    : {}),
                beforeClose: async (action, instance, done) => {
                    if (action === 'confirm') {
                        // console.log('Confirm clicked, formApiRef.value:', formApiRef.value)

                        // 等待下一个tick，确保表单完全加载
                        await nextTick()

                        // 尝试从DOM中获取表单API
                        if (!formApiRef.value) {
                            const formElement = document.querySelector('.form-container')
                            if (formElement) {
                                const formInstance = (formElement as any).__vueParentComponent?.ctx
                                if (formInstance && formInstance.$f) {
                                    formApiRef.value = formInstance.$f
                                    // console.log('Found form API from DOM:', formApiRef.value)
                                }
                            }
                        }

                        if (formApiRef.value) {
                            formApiRef.value.submit((formData: any) => {
                                // 必须先铺服务端下发的 formData（含 id 等），再用用户当前表单覆盖。
                                // 若顺序反了，data.formData 里的 null（如 smsAliTempid）会盖掉用户刚输入的值。
                                const submitData = {
                                    ...(data.formData || {}),
                                    ...formData,
                                }

                                // 确保pid字段是整数类型
                                if (submitData.pid !== undefined && submitData.pid !== null) {
                                    submitData.pid = Number(submitData.pid);
                                }

                                // 商品选择(goodList)回传 { src, id }，提交时取 id 作为 productId
                                if (submitData.productId != null && typeof submitData.productId === 'object' && !Array.isArray(submitData.productId)) {
                                    submitData.productId = (submitData.productId as any).id ?? (submitData.productId as any).productId ?? submitData.productId;
                                }
                                if (submitData.productId != null) {
                                    submitData.productId = Number(submitData.productId);
                                }

                                // console.log('Submitting data:', submitData);

                                http({
                                    url: data.action,
                                    method: data.method.toLowerCase(),
                                    data: submitData
                                }).then((submitResponse: any) => {
                                    // console.log('Submit response:', submitResponse)

                                    // axios响应拦截器已经返回了res.data.data，所以这里直接使用
                                    const result = submitResponse

                                    // 适配不同的响应格式
                                    const status = result.status ||  200  // 如果没有status/code，默认为200
                                    const message = result.message || result.msg || '操作成功'
                                    const responseData = result.data

                                    // console.log('Processed result:', { status, message, data: responseData })

                                    if (status === 200) {
                                        ElMessage.success(message || '操作成功')
                                        callback?.(submitData)
                                        onSuccess?.({ status, msg: message, data: responseData })
                                        resolve({ status, msg: message, data: responseData })
                                        done()
                                    } else {
                                        ElMessage.error(message || '操作失败')
                                        onError?.({ status, msg: message, data: responseData })
                                        reject({ status, msg: message, data: responseData })
                                    }
                                }).catch((err: any) => {
                                    console.error('Submit error:', err)
                                    ElMessage.error(err.message || '操作失败')
                                    onError?.(err)
                                    reject(err)
                                })
                            })
                        } else {
                            console.error('No form API available')
                            ElMessage.error('表单未初始化')
                            reject(new Error('表单未初始化'))
                        }
                    } else {
                        done()
                    }
                }
            }).catch((err: any) => {
                if (err !== 'cancel') {
                    ElMessage.error(err.message || '操作失败')
                    onError?.(err)
                    reject(err)
                }
            })
        }).catch((err: any) => {
            ElMessage.error(err.message || '加载表单失败')
            onError?.(err)
            reject(err)
        })
    })
}

export type { Api, Options, ApiResponse, ModalFormConfig, Rule }
